import { useAnalytics } from "@/hooks/useAnalytics";

// Safe wrapper that handles errors when called outside Router context
const safeUseAnalytics = () => {
  try {
    return useAnalytics();
  } catch (error) {
    console.debug("[Analytics] Hook call failed, returning no-op:", error);
    return {
      trackEvent: () => {},
      trackConversion: () => {},
      trackSearch: () => {},
      trackPageView: () => {},
      getSession: () => null,
      resetAnalytics: () => {},
      isInitialized: false,
    };
  }
};

export const useTrackingHelpers = () => {
  const { trackEvent, trackConversion, trackSearch, trackPageView } =
    safeUseAnalytics();

  const trackButtonClick = (buttonName: string, location?: string) => {
    trackEvent("click", window.location.pathname, {
      buttonName,
      location,
      timestamp: Date.now(),
    });
  };

  const trackFormSubmission = (formName: string, success: boolean = true) => {
    trackEvent("form_submit", window.location.pathname, {
      formName,
      success,
      timestamp: Date.now(),
    });
  };

  const trackBookingAttempt = (
    service: string,
    origin?: string,
    destination?: string,
  ) => {
    trackConversion("booking_attempt", 0, {
      service,
      origin,
      destination,
      timestamp: Date.now(),
    });
  };

  const trackPhoneCall = (source: string) => {
    trackConversion("phone_call", 0, {
      source,
      timestamp: Date.now(),
    });
  };

  const trackAppDownload = (platform: "ios" | "android", source: string) => {
    trackConversion("app_download", 1, {
      platform,
      source,
      timestamp: Date.now(),
    });
  };

  const trackSearchQuery = (
    query: string,
    results?: number,
    category?: string,
  ) => {
    trackSearch(query, results);
    trackEvent("search", window.location.pathname, {
      query,
      results,
      category,
      timestamp: Date.now(),
    });
  };

  const trackPageViewHelper = (page: string, additionalData?: any) => {
    try {
      trackPageView?.(page, additionalData);
    } catch {}
  };

  return {
    trackButtonClick,
    trackFormSubmission,
    trackBookingAttempt,
    trackPhoneCall,
    trackAppDownload,
    trackSearchQuery,
    trackPageView: trackPageViewHelper,
  };
};
